import {Fragment, useCallback, useEffect, useState} from "react";
import {Layout} from "./Layout";
import {useParams} from "react-router-dom";
import {get, post, put} from "../../js/api";
import {OrderInformation} from "../OrderInformation";
import {Button} from "../form/Button";
import { loadStripe } from "@stripe/stripe-js/pure";
import { Elements } from "@stripe/react-stripe-js";
import CheckoutForm from "../CheckoutForm";
import {showError, showMessage} from "../../js/main";
import {mapState} from "../../view/orders/UpdateOrder";
import {loadConfiguration} from "../../App";
import {stripeState} from "../../js/constants";
import {enqueue} from "../../js/errorhandling";

let stripePromise;
let stripeLoadErrorLogged = false;

function logStripeLoadError(error) {
    if (stripeLoadErrorLogged) {
        return;
    }

    stripeLoadErrorLogged = true;
    enqueue({
        url: document.location.href,
        name: error?.name || 'StripeLoadError',
        message: error?.message || String(error),
        stack: error?.stack,
        source: 'stripe.load',
        timestamp: Date.now(),
    });
}

function initStripe() {
    if (!stripePromise) {
        stripePromise = loadConfiguration().then(config =>
            loadStripe(config.default['stripePublicKey'])
        ).catch((error) => {
            logStripeLoadError(error);
            return null;
        });
    }
    return stripePromise;
}

export const Payment = () => {
    const stripe = initStripe();

    const [currStripeState, setCurrStripeState] = useState(stripeState.INIT)
    const {paymentId, done} = useParams();

    const [payment, setPayment] = useState(null);
    const [clientSecret, setClientSecret] = useState();
    const [submitting, setSubmitting] = useState();

    const [message, setMessage] = useState('');

    const loadPayment = useCallback(() => get("/payments/"+encodeURIComponent(paymentId)).then(setPayment), [paymentId]);

    useEffect(() => {
        loadPayment();

        stripe
            .then((stripeInstance) => setCurrStripeState(stripeInstance ? stripeState.SUCCESS : stripeState.FAILED))
            .catch(() => setCurrStripeState(stripeState.FAILED));

    }, [loadPayment, stripe]);

    const requestClientSecret = () => {
        setSubmitting(true);
        post("/payments/"+encodeURIComponent(paymentId)+"/intents", null).then(setClientSecret).finally(() => setSubmitting(false));
    }

    useEffect(() => {
        if(done){
            const queryParams = new URLSearchParams(window.location.search);
            if(queryParams.get("redirect_status") == "succeeded"){
                put("/payments/"+encodeURIComponent(paymentId)+"/intents/state/PENDING", null).then(() => {
                    loadPayment().then(() => {
                        const message = "Vielen Dank, die Zahlung wird verarbeitet. Sie werden per E-Mail informiert";
                        showMessage(message);
                        setMessage(message);
                    }).catch(() => {
                        const message = "Vielen Dank, die Zahlung wird verarbeitet. Der Zahlungsstatus konnte nicht aktualisiert werden. Bitte laden Sie die Seite neu.";
                        showError("Der Zahlungsstatus konnte nicht aktualisiert werden. Bitte laden Sie die Seite neu.");
                        setMessage(message);
                    });
                }).catch(() => {
                    const message = "Bei Verarbeitung der Zahlung ist ein Fehler aufgetreten.";
                    showError(message);
                    setMessage(message);
                });
            }else if(queryParams.get("redirect_status")){
                const message = "Bei der Zahlung ist ein Fehler aufgetreten, bitte beachten Sie alle Meldungen und probieren Sie es bei Bedarf nochmals.";
                showError(message);
                setMessage(message);
            }
        }
    }, [done, loadPayment, paymentId])

    const appearance = {
        theme: 'stripe',
        variables: {
            colorPrimary: '#253646',
        },
    };
    const options = {
        clientSecret,
        appearance,
    };

    return (
        <Layout >
            {!payment && <Fragment>
                <h2 className="mt-1d5 mb-03">Ihre Bestellung wird geladen...</h2>
            </Fragment>}
            {payment && <Fragment>
                <OrderInformation order={payment.orderInfo} />
                <h2 className="mt-1d5 mb-03">Bezahlung</h2>
                <table>
                    <tr>
                        <td width="200">Status</td>
                        <td>{mapState(payment.paymentInfo.paymentState)}</td>
                    </tr>
                </table>
            </Fragment>
            }
            {payment && !done &&
                <Fragment>
                    {!clientSecret && (payment.paymentInfo.paymentState == 'AWAIT_USER' || payment.paymentInfo.paymentState == 'FAILED') &&
                    <Button submitting={submitting} width={250} onClick={requestClientSecret}>Bezahlung starten</Button>
                    }
                    {clientSecret && currStripeState === stripeState.SUCCESS &&
                        <Elements options={options} stripe={stripe}>
                            <CheckoutForm />
                        </Elements>
                    }
                    {currStripeState === stripeState.FAILED && <>
                        <h2>Bezahlung initialisieren fehlgeschlagen</h2>
                        <p>Bitte laden Sie die Seite neu. Bei wiederholten Problemen verwenden Sie den Chat in der Bestellung, um mit unserem Team Kontakt aufzunehmen, dann helfen wir Ihnen gerne weiter.</p>
                    </>}
                </Fragment>
            }
            {payment && done && <Fragment>
                <p>{message}</p>
            </Fragment>}
        </Layout>
    );

};
